import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { fadeUp, viewportOnce } from "../lib/motion";

const PLAN_OPTIONS = ["YES", "NO", "NOT SURE"];
const CONTACT_OPTIONS = ["EMAIL", "PHONE", "WHATSAPP"];

const Field = ({
    label,
    name,
    testid,
    type = "text",
    optional = false,
    value,
    onChange,
    textarea = false,
}) => (
    <div>
        <label
            htmlFor={name}
            className="flex items-baseline justify-between font-mono text-[13px] tracking-[0.22em] text-cad"
        >
            <span>
                {label}
                {optional && <span className="ml-2 text-cad/70">OPTIONAL</span>}
            </span>
        </label>
        {textarea ? (
            <textarea
                id={name}
                name={name}
                data-testid={testid}
                rows={3}
                maxLength={500}
                value={value}
                onChange={onChange}
                className="mt-2 w-full resize-none border-b border-white/25 bg-transparent py-3 text-[16px] text-white outline-none transition-colors duration-300 placeholder:text-cad/60 focus:border-accent"
                placeholder="—"
            />
        ) : (
            <input
                id={name}
                name={name}
                type={type}
                required={!optional}
                data-testid={testid}
                value={value}
                onChange={onChange}
                className="mt-2 w-full border-b border-white/25 bg-transparent py-3 text-[16px] text-white outline-none transition-colors duration-300 placeholder:text-cad/60 focus:border-accent"
                placeholder="—"
            />
        )}
    </div>
);

const RadioGroup = ({ label, options, value, onChange, name }) => (
    <div>
        <p className="font-mono text-[13px] tracking-[0.22em] text-cad">
            {label}
        </p>
        <div role="radiogroup" aria-label={label} className="mt-3 flex flex-wrap gap-2.5">
            {options.map((o) => (
                <button
                    key={o}
                    type="button"
                    role="radio"
                    aria-checked={value === o}
                    data-testid={`pilot-${name}-${o.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => onChange(o)}
                    className={`border px-5 py-2.5 font-mono text-[13px] tracking-[0.2em] transition-all duration-300 ${
                        value === o
                            ? "border-accent bg-accent/10 text-accent"
                            : "border-white/25 text-mist hover:border-white/50 hover:text-white"
                    }`}
                >
                    {o}
                </button>
            ))}
        </div>
    </div>
);

const PilotForm = () => {
    const [form, setForm] = useState({
        fullName: "",
        email: "",
        phone: "",
        projectLocation: "",
        hasPlan: "",
        contactMethod: "",
        projectNotes: "",
    });
    const [status, setStatus] = useState("idle");
    const [reference, setReference] = useState("");

    const set = (key) => (e) =>
        setForm((f) => ({ ...f, [key]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (status === "submitting") return;
        setStatus("submitting");
        try {
            const res = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/pilot-applications`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        full_name: form.fullName.trim(),
                        email: form.email.trim(),
                        phone: form.phone.trim(),
                        project_location: form.projectLocation.trim(),
                        has_plan: form.hasPlan,
                        contact_method: form.contactMethod,
                        project_notes: form.projectNotes.trim(),
                    }),
                },
            );
            if (!res.ok) throw new Error("failed");
            const data = await res.json();
            setReference(data.id);
            setStatus("success");
        } catch {
            setStatus("error");
        }
    };

    return (
        <section
            id="apply"
            data-testid="pilot-form-section"
            className="relative bg-ink py-24 lg:py-36"
        >
            <div className="mx-auto max-w-[1600px] px-6 md:px-10 lg:px-16">
                <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={viewportOnce}
                    className="flex items-center gap-6"
                >
                    <p className="font-mono text-[13px] tracking-[0.3em] text-mist">
                        APPLICATION
                    </p>
                    <span className="h-px flex-1 bg-white/10" />
                    <p className="font-mono text-[13px] tracking-[0.3em] text-cad">
                        FORM 01
                    </p>
                </motion.div>

                <div className="mt-14 grid gap-14 lg:grid-cols-12">
                    <motion.div
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="show"
                        viewport={viewportOnce}
                        className="lg:col-span-4"
                    >
                        <h2 className="font-display text-3xl font-semibold leading-[1.1] text-white md:text-4xl">
                            YOUR APPLICATION
                            <span className="text-accent">.</span>
                        </h2>
                        <p className="mt-6 max-w-xs text-[15px] leading-relaxed text-mist">
                            A few details about you and your project. We use
                            them only to assess your pilot application.
                        </p>
                        <p className="mt-8 font-mono text-[13px] leading-relaxed tracking-[0.2em] text-cad">
                            ALL FIELDS REQUIRED UNLESS MARKED OPTIONAL.
                            <span className="mt-2 block">
                                POPIA-CONSCIOUS DATA HANDLING.
                            </span>
                        </p>
                    </motion.div>

                    <motion.div
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="show"
                        viewport={viewportOnce}
                        className="lg:col-span-8"
                    >
                        {status === "success" ? (
                            <div
                                data-testid="pilot-success"
                                className="border border-accent/50 bg-[#0C1428] p-10 text-center md:p-14"
                            >
                                <span className="mx-auto flex h-12 w-12 items-center justify-center border border-accent/70">
                                    <Check className="h-5 w-5 text-accent" />
                                </span>
                                <h3 className="mt-8 font-display text-2xl font-semibold tracking-tight text-white md:text-3xl">
                                    APPLICATION RECEIVED
                                    <span className="text-accent">.</span>
                                </h3>
                                <p className="mt-4 font-mono text-[13px] tracking-[0.22em] text-mist">
                                    REFERENCE
                                </p>
                                <p
                                    data-testid="pilot-reference"
                                    className="mt-2 font-mono text-[15px] tracking-[0.14em] text-accent"
                                >
                                    {reference}
                                </p>
                                <p className="mx-auto mt-8 max-w-sm text-[15px] leading-relaxed text-mist">
                                    Keep this reference for your records. If
                                    your application is selected, we'll make
                                    contact using your preferred channel.
                                </p>
                                <Link
                                    to="/"
                                    data-testid="pilot-success-home"
                                    className="mt-10 inline-flex items-center gap-2 border border-white/25 px-6 py-3 text-sm text-white transition-colors duration-300 hover:border-accent hover:text-accent"
                                >
                                    Back to Home
                                </Link>
                            </div>
                        ) : (
                            <form
                                data-testid="pilot-form"
                                onSubmit={submit}
                                className="border border-white/15 bg-[#0C1428] p-8 md:p-10"
                            >
                                <div className="grid gap-8 sm:grid-cols-2">
                                    <Field
                                        label="FULL NAME"
                                        name="fullName"
                                        testid="pilot-input-name"
                                        value={form.fullName}
                                        onChange={set("fullName")}
                                    />
                                    <Field
                                        label="EMAIL ADDRESS"
                                        name="email"
                                        testid="pilot-input-email"
                                        type="email"
                                        value={form.email}
                                        onChange={set("email")}
                                    />
                                    <Field
                                        label="PHONE NUMBER"
                                        name="phone"
                                        testid="pilot-input-phone"
                                        type="tel"
                                        value={form.phone}
                                        onChange={set("phone")}
                                    />
                                    <Field
                                        label="PROJECT LOCATION"
                                        name="projectLocation"
                                        testid="pilot-input-location"
                                        value={form.projectLocation}
                                        onChange={set("projectLocation")}
                                    />
                                </div>

                                <div className="mt-10 grid gap-10 sm:grid-cols-2">
                                    <RadioGroup
                                        label="DO YOU HAVE AN ARCHITECTURAL PLAN?"
                                        name="plan"
                                        options={PLAN_OPTIONS}
                                        value={form.hasPlan}
                                        onChange={(v) =>
                                            setForm((f) => ({
                                                ...f,
                                                hasPlan: v,
                                            }))
                                        }
                                    />
                                    <RadioGroup
                                        label="PREFERRED CONTACT METHOD"
                                        name="contact"
                                        options={CONTACT_OPTIONS}
                                        value={form.contactMethod}
                                        onChange={(v) =>
                                            setForm((f) => ({
                                                ...f,
                                                contactMethod: v,
                                            }))
                                        }
                                    />
                                </div>

                                <div className="mt-10">
                                    <Field
                                        label="TELL US BRIEFLY ABOUT YOUR PROJECT"
                                        name="projectNotes"
                                        testid="pilot-input-notes"
                                        optional
                                        textarea
                                        value={form.projectNotes}
                                        onChange={set("projectNotes")}
                                    />
                                </div>

                                {status === "error" && (
                                    <p
                                        data-testid="pilot-error"
                                        className="mt-6 border border-red-500/40 bg-red-500/10 px-4 py-3 font-mono text-[13px] tracking-[0.18em] text-red-300"
                                    >
                                        SOMETHING WENT WRONG — PLEASE TRY
                                        AGAIN.
                                    </p>
                                )}

                                <button
                                    type="submit"
                                    data-testid="pilot-submit"
                                    disabled={status === "submitting"}
                                    className="mt-10 flex w-full items-center justify-center gap-3 bg-accent px-8 py-5 text-[15px] font-semibold tracking-[0.1em] text-white transition-colors duration-300 hover:bg-[#ff7026] disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                    {status === "submitting"
                                        ? "SUBMITTING…"
                                        : "APPLY FOR THE DURBAN PILOT"}
                                    {status !== "submitting" && (
                                        <ArrowUpRight className="h-4 w-4" />
                                    )}
                                </button>
                            </form>
                        )}
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default PilotForm;
